import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Card,
    CardContent,
    Grid,
    Backdrop,
    CircularProgress,
    Tabs,
    Tab,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Divider,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    RefreshSharp,
    Save,
    Business,
    Phone,
    HealthAndSafety,
    Psychology,
    Edit,
} from "@mui/icons-material";
import Cookies from "js-cookie";
import { Navigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import BusinessProfilePanel from "./settings/BusinessProfilePanel";
import ProfileDescriptionCard from "./settings/ProfileDescriptionCard";
import ProfilePictureCard from "./settings/ProfilePictureCard";

const QUALITY_CHIP_COLOR = {
    GREEN: "success",
    YELLOW: "warning",
    RED: "error",
    UNKNOWN: "default",
};

const parseUserId = () => {
    const raw = Cookies.get("userId");
    if (!raw) return null;
    try {
        return String(JSON.parse(raw));
    } catch {
        return String(raw);
    }
};

const InfoRow = ({ label, value }) => {
    if (value == null || value === "") return null;
    return (
        <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
                {value}
            </Typography>
        </Grid>
    );
};

const WhatsappBusinessSettings = () => {
    const isWbAdmin = useMemo(
        () => (Cookies.get("userRole") || "").split(",").map((r) => r.trim()).includes("WB-ADMIN"),
        []
    );

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const notify = useNotification();

    const [waba, setWaba] = useState(null);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [selectedPhoneId, setSelectedPhoneId] = useState("");
    const [phoneDetails, setPhoneDetails] = useState(null);
    const [phoneHealth, setPhoneHealth] = useState(null);

    const [profile, setProfile] = useState(null);

    const [businessDetails, setBusinessDetails] = useState(null);
    const [businessDetailInput, setBusinessDetailInput] = useState("");

    const profilePath = (phoneId) =>
        phoneId
            ? `${base_url}/whatsapp-business/whatsapp-settings/phone-number/${phoneId}/business-profile`
            : `${base_url}/whatsapp-business/whatsapp-settings/business-profile`;

    const descriptionPath = (phoneId) =>
        phoneId
            ? `${base_url}/whatsapp-business/whatsapp-settings/phone-number/${phoneId}/business-profile/description`
            : `${base_url}/whatsapp-business/whatsapp-settings/business-profile/description`;

    const picturePath = (phoneId) =>
        phoneId
            ? `${base_url}/whatsapp-business/whatsapp-settings/phone-number/${phoneId}/business-profile/picture`
            : `${base_url}/whatsapp-business/whatsapp-settings/business-profile/picture`;

    const fetchWaba = useCallback(async () => {
        const response = await axiosInstance.get(`${base_url}/whatsapp-business/whatsapp-settings/waba`);
        setWaba(response.data);
        return response.data;
    }, []);

    const fetchPhoneNumbers = useCallback(async () => {
        const response = await axiosInstance.get(
            `${base_url}/whatsapp-business/whatsapp-settings/phone-numbers`
        );
        const list = response.data?.data || [];
        setPhoneNumbers(list);
        setSelectedPhoneId((prev) => prev || (list[0]?.id ?? ""));
        return list;
    }, []);

    const fetchPhoneDetails = useCallback(async (phoneId) => {
        const id = phoneId || selectedPhoneId;
        const url = id
            ? `${base_url}/whatsapp-business/whatsapp-settings/phone-number/${id}`
            : `${base_url}/whatsapp-business/whatsapp-settings/phone-number`;
        const response = await axiosInstance.get(url);
        setPhoneDetails(response.data);
        return response.data;
    }, [selectedPhoneId]);

    const fetchPhoneHealth = useCallback(async (phoneId) => {
        const id = phoneId || selectedPhoneId;
        const url = id
            ? `${base_url}/whatsapp-business/whatsapp-settings/phone-number/${id}/health`
            : `${base_url}/whatsapp-business/whatsapp-settings/phone-number-health`;
        const response = await axiosInstance.get(url);
        setPhoneHealth(response.data);
        return response.data;
    }, [selectedPhoneId]);

    const fetchBusinessProfile = useCallback(async (phoneId) => {
        const id = phoneId ?? selectedPhoneId;
        const response = await axiosInstance.get(profilePath(id || null));
        const data = response.data?.data?.[0] || response.data;
        setProfile(data);
        return data;
    }, [selectedPhoneId]);

    const fetchBusinessDetails = useCallback(async () => {
        const response = await axiosInstance.get(`${base_url}/whatsapp-business/business-details`);
        if (response.data?.success) {
            const data = response.data.data;
            setBusinessDetails(data);
            setBusinessDetailInput(data?.business_detail || "");
        } else {
            setBusinessDetails(null);
            setBusinessDetailInput("");
        }
    }, []);

    const loadInitialData = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([fetchWaba(), fetchPhoneNumbers(), fetchBusinessDetails()]);
        } catch (error) {
            notify(
                error.response?.data?.message || error.response?.data?.error || "Failed to load settings",
                "error"
            );
        } finally {
            setLoading(false);
        }
    }, [fetchWaba, fetchPhoneNumbers, fetchBusinessDetails, notify]);

    useEffect(() => {
        if (isWbAdmin) {
            loadInitialData();
        }
    }, [isWbAdmin, loadInitialData]);

    useEffect(() => {
        if (!isWbAdmin || !selectedPhoneId) return;
        const loadPhoneData = async () => {
            setLoading(true);
            try {
                await Promise.all([
                    fetchPhoneDetails(selectedPhoneId),
                    fetchPhoneHealth(selectedPhoneId),
                    fetchBusinessProfile(selectedPhoneId),
                ]);
            } catch (error) {
                notify(
                    error.response?.data?.message || error.response?.data?.error || "Failed to load phone data",
                    "error"
                );
            } finally {
                setLoading(false);
            }
        };
        loadPhoneData();
    }, [selectedPhoneId, isWbAdmin, fetchPhoneDetails, fetchPhoneHealth, fetchBusinessProfile, notify]);

    const handleRefreshAll = async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchWaba(),
                fetchPhoneNumbers(),
                selectedPhoneId ? fetchPhoneDetails(selectedPhoneId) : Promise.resolve(),
                selectedPhoneId ? fetchPhoneHealth(selectedPhoneId) : Promise.resolve(),
                selectedPhoneId ? fetchBusinessProfile(selectedPhoneId) : Promise.resolve(),
                fetchBusinessDetails(),
            ]);
            notify("Settings refreshed", "success");
        } catch (error) {
            notify(error.response?.data?.message || "Refresh failed", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProfile = async (payload) => {
        setLoading(true);
        try {
            await axiosInstance.post(profilePath(selectedPhoneId || null), payload);
            notify("Business profile updated", "success");
            await fetchBusinessProfile(selectedPhoneId);
            return true;
        } catch (error) {
            notify(error.response?.data?.message || error.response?.data?.error || "Failed to update profile", "error");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDescription = async (description) => {
        setLoading(true);
        try {
            await axiosInstance.post(descriptionPath(selectedPhoneId || null), { description });
            notify("Description updated", "success");
            await fetchBusinessProfile(selectedPhoneId);
        } catch (error) {
            notify(
                error.response?.data?.message || error.response?.data?.error || "Failed to update description",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSavePicture = async (file) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("profile_picture", file);
            await axiosInstance.post(picturePath(selectedPhoneId || null), formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            notify("Profile picture updated", "success");
            await fetchBusinessProfile(selectedPhoneId);
        } catch (error) {
            notify(
                error.response?.data?.message || error.response?.data?.error || "Failed to update profile picture",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSaveBusinessDetails = async () => {
        const detail = businessDetailInput.trim();
        if (!detail) {
            notify("Please enter business details for the bot", "error");
            return;
        }
        const userId = parseUserId();
        if (!userId) {
            notify("User session not found. Please log in again.", "error");
            return;
        }
        setLoading(true);
        try {
            const response = await axiosInstance.put(`${base_url}/whatsapp-business/business-details`, {
                user_id: userId,
                business_detail: detail,
            });
            if (response.data?.success) {
                notify("Business details saved", "success");
                setBusinessDetails(response.data.data);
            } else {
                notify(response.data?.message || "Failed to save business details", "error");
            }
        } catch (error) {
            notify(error.response?.data?.message || "Failed to save business details", "error");
        } finally {
            setLoading(false);
        }
    };

    if (!isWbAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <Box sx={WB_SX.page}>
            <Box sx={WB_SX.headerRow}>
                <Box>
                    <Typography variant="h6" sx={WB_SX.pageTitle}>
                        WhatsApp Business Settings
                    </Typography>
                    <Typography variant="body2" sx={WB_SX.pageSubtitle}>
                        Manage your WABA account, phone numbers, public profile, and bot context.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshSharp />}
                    onClick={handleRefreshAll}
                    sx={WB_SX.secondaryButton}
                    disabled={loading}
                >
                    Refresh all
                </Button>
            </Box>

            <Backdrop
                sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: `1px solid ${WB_UI.color.border}`, px: 1 }}
                >
                    <Tab icon={<Business fontSize="small" />} iconPosition="start" label="WABA Account" />
                    <Tab icon={<Phone fontSize="small" />} iconPosition="start" label="Phone Numbers" />
                    <Tab icon={<Edit fontSize="small" />} iconPosition="start" label="Business Profile" />
                    <Tab icon={<Psychology fontSize="small" />} iconPosition="start" label="Bot Context" />
                </Tabs>

                <CardContent sx={WB_SX.cardContent}>
                    {tab === 0 && (
                        <Box>
                            {!waba ? (
                                <Alert severity="info">
                                    No WABA details loaded. Ensure your WhatsApp Business configuration is active, then
                                    refresh.
                                </Alert>
                            ) : (
                                <Grid container spacing={2}>
                                    <InfoRow label="WABA ID" value={waba.id} />
                                    <InfoRow label="Account Name" value={waba.name} />
                                    <InfoRow label="Currency" value={waba.currency} />
                                    <InfoRow label="Timezone ID" value={waba.timezone_id} />
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Review Status
                                        </Typography>
                                        <Chip
                                            label={waba.account_review_status || "—"}
                                            size="small"
                                            color={waba.account_review_status === "APPROVED" ? "success" : "default"}
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                    )}

                    {tab === 1 && (
                        <Box>
                            {phoneNumbers.length === 0 ? (
                                <Alert severity="info">No connected phone numbers found for this account.</Alert>
                            ) : (
                                <>
                                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: WB_UI.color.tableHeadBg }}>
                                                    <TableCell sx={WB_SX.tableHeadCell}>Verified Name</TableCell>
                                                    <TableCell sx={WB_SX.tableHeadCell}>Display Number</TableCell>
                                                    <TableCell sx={WB_SX.tableHeadCell}>Quality</TableCell>
                                                    <TableCell sx={WB_SX.tableHeadCell}>Verification</TableCell>
                                                    <TableCell sx={WB_SX.tableHeadCell} align="right">
                                                        Actions
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {phoneNumbers.map((phone) => {
                                                    const selected = phone.id === selectedPhoneId;
                                                    return (
                                                        <TableRow
                                                            key={phone.id}
                                                            hover
                                                            selected={selected}
                                                            onClick={() => setSelectedPhoneId(phone.id)}
                                                            sx={{ cursor: "pointer", ...WB_SX.tableRow }}
                                                        >
                                                            <TableCell>{phone.verified_name || "—"}</TableCell>
                                                            <TableCell>{phone.display_phone_number || phone.id}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={phone.quality_rating || "UNKNOWN"}
                                                                    size="small"
                                                                    color={QUALITY_CHIP_COLOR[phone.quality_rating] || "default"}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={phone.code_verification_status || "—"}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Tooltip title="Load details & health">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedPhoneId(phone.id);
                                                                        }}
                                                                    >
                                                                        <HealthAndSafety fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {selectedPhoneId && (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={WB_SX.sectionTitle} gutterBottom>
                                                    Phone Details
                                                </Typography>
                                                {phoneDetails ? (
                                                    <Grid container spacing={1.5}>
                                                        <InfoRow label="Phone Number ID" value={phoneDetails.id} />
                                                        <InfoRow
                                                            label="Display Number"
                                                            value={phoneDetails.display_phone_number}
                                                        />
                                                        <InfoRow label="Verified Name" value={phoneDetails.verified_name} />
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Quality Rating
                                                            </Typography>
                                                            <Box sx={{ mt: 0.5 }}>
                                                                <Chip
                                                                    label={phoneDetails.quality_rating || "UNKNOWN"}
                                                                    size="small"
                                                                    color={
                                                                        QUALITY_CHIP_COLOR[phoneDetails.quality_rating] ||
                                                                        "default"
                                                                    }
                                                                />
                                                            </Box>
                                                        </Grid>
                                                        <InfoRow
                                                            label="Verification"
                                                            value={phoneDetails.code_verification_status}
                                                        />
                                                        <InfoRow label="Name Status" value={phoneDetails.name_status} />
                                                        <InfoRow label="Platform" value={phoneDetails.platform_type} />
                                                    </Grid>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Select a phone number to view details.
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={WB_SX.sectionTitle} gutterBottom>
                                                    Messaging Health
                                                </Typography>
                                                {phoneHealth ? (
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 2,
                                                            border: `1px solid ${WB_UI.color.border}`,
                                                            bgcolor: "rgba(0,0,0,0.02)",
                                                        }}
                                                    >
                                                        <Chip
                                                            icon={<HealthAndSafety />}
                                                            label={`Quality: ${phoneHealth.quality_rating || "UNKNOWN"}`}
                                                            color={
                                                                QUALITY_CHIP_COLOR[phoneHealth.quality_rating] || "default"
                                                            }
                                                            sx={{ mb: 1 }}
                                                        />
                                                        <Typography variant="body2" color="text.secondary">
                                                            Phone ID: {phoneHealth.id || selectedPhoneId}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Health data will appear when a phone is selected.
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Box>
                    )}

                    {tab === 2 && (
                        <Box>
                            <BusinessProfilePanel
                                profile={profile}
                                phoneNumbers={phoneNumbers}
                                selectedPhoneId={selectedPhoneId}
                                onPhoneChange={setSelectedPhoneId}
                                onSave={handleSaveProfile}
                                loading={loading}
                            />

                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={6}>
                                    <ProfileDescriptionCard
                                        initialDescription={profile?.description || ""}
                                        onSave={handleSaveDescription}
                                        loading={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ProfilePictureCard
                                        currentPictureUrl={profile?.profile_picture_url}
                                        onSave={handleSavePicture}
                                        loading={loading}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {tab === 3 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Business context used by the bot and agent. This is stored in your account and can be
                                referenced during automated replies.
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <TextField
                                fullWidth
                                label="Business Details"
                                value={businessDetailInput}
                                onChange={(e) => setBusinessDetailInput(e.target.value)}
                                multiline
                                rows={8}
                                placeholder="Describe your business, products, services, location, policies, etc."
                                helperText={
                                    businessDetails?.updated_at
                                        ? `Last updated: ${new Date(businessDetails.updated_at).toLocaleString()}`
                                        : "No saved details yet"
                                }
                            />
                            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={handleSaveBusinessDetails}
                                    sx={WB_SX.primaryButton}
                                >
                                    Save Bot Context
                                </Button>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default WhatsappBusinessSettings;
